import { useState } from 'react'
import {
  apiErrorMessage,
  useAdminPayoutQueue,
  useApprovePayout,
  useMarkPayoutPaid,
  useRejectPayout,
  type PayoutQueueParams,
} from '../lib/api'
import { fmtDateTime, fmtEur } from '../lib/format'

const STATUS_TONE: Record<string, string> = {
  requested: 'admin-pill admin-pill--warning',
  approved:  'admin-pill admin-pill--info',
  paid:      'admin-pill admin-pill--success',
  rejected:  'admin-pill admin-pill--danger',
}

export function PayoutQueuePage() {
  const [params, setParams] = useState<PayoutQueueParams>({ page: 1, perPage: 50 })
  const q = useAdminPayoutQueue(params)
  const approve = useApprovePayout()
  const reject  = useRejectPayout()
  const markPaid = useMarkPayoutPaid()
  const [working, setWorking] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const onApprove = (id: string) => {
    setWorking(id); setErrorMsg(null)
    approve.mutate({ id }, { onSettled: () => setWorking(null), onError: (err) => setErrorMsg(apiErrorMessage(err)) })
  }
  const onReject = (id: string) => {
    const reason = window.prompt('Reject reason (min 3 char):')
    if (!reason || reason.length < 3) return
    setWorking(id); setErrorMsg(null)
    reject.mutate({ id, reason }, { onSettled: () => setWorking(null), onError: (err) => setErrorMsg(apiErrorMessage(err)) })
  }
  const onMarkPaid = (id: string) => {
    const externalPaymentId = window.prompt('External payment ID:')
    if (!externalPaymentId) return
    setWorking(id); setErrorMsg(null)
    markPaid.mutate({ id, externalPaymentId }, { onSettled: () => setWorking(null), onError: (err) => setErrorMsg(apiErrorMessage(err)) })
  }

  return (
    <>
      <header className="page-header">
        <div className="page-header__title-row">
          <h1 className="page-header__title">Payouts</h1>
          <span className="page-header__subtitle">Approve, reject, or mark a payout as paid.</span>
        </div>
      </header>

      <div className="filter-bar" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="filter-bar__group">
          <label className="field__label" htmlFor="payout-status">Status</label>
          <select
            id="payout-status"
            className="field__input"
            style={{ width: '180px' }}
            value={params.status ?? ''}
            onChange={(e) => setParams((p) => ({ ...p, status: e.target.value || undefined, page: 1 }))}
          >
            <option value="">All</option>
            <option value="requested">Requested</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {errorMsg && (
        <div className="auth-card__error" role="alert" style={{ display: 'flex', marginBottom: 'var(--space-3)' }}>{errorMsg}</div>
      )}

      <div className="admin-table">
        <table>
          <thead>
            <tr>
              <th>Trader</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Requested</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>Loading…</td></tr>
            )}
            {q.data && q.data.payouts.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-tertiary)' }}>No payout requests.</td></tr>
            )}
            {q.data && q.data.payouts.map((p) => {
              const busy = working === p.id
              return (
                <tr key={p.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.user?.displayName ?? '—'}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      {p.user?.email ?? p.userId}
                    </div>
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{fmtEur(p.amount)}</td>
                  <td><span className={STATUS_TONE[p.status] ?? 'admin-pill'}>{p.status}</span></td>
                  <td>{fmtDateTime(p.requestedAt)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {p.status === 'requested' && (
                      <>
                        <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => onApprove(p.id)}
                          style={{ marginRight: 'var(--space-2)' }}>
                          Approve
                        </button>
                        <button type="button" className="btn btn--danger btn--sm" disabled={busy} onClick={() => onReject(p.id)}>
                          Reject
                        </button>
                      </>
                    )}
                    {p.status === 'approved' && (
                      <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => onMarkPaid(p.id)}>
                        Mark paid
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
